package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.info
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.button
import com.siriusxm.pia.components.simpleCheckBox
import com.siriusxm.pia.components.spinner
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.getIncomingEntityUi
import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.EntityAccessControl
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.rememberCanPublishPartial
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import kotlin.time.Clock

/** The partial the access-control editor publishes. */
private const val ACCESS_CONTROL_PARTIAL_ID = "accessControl"

private val json = Json {
    ignoreUnknownKeys = true
}

/**
 * The access-control override editor for an entity: an "override" checkbox, the visible /
 * discoverable / recommendable flags, and save and reset buttons. Saving publishes the
 * `accessControl` partial as the console's own producer (UCA-1152); the aggregator authorizes the
 * user through AVP. The save controls are shown only when AVP allows that partial for this entity,
 * and only when the console producer is configured for it. That pre-check hides controls; the
 * aggregator still decides on save.
 */
@Composable
fun entityAccessControls(aggregator: AggregatorService, entity: Entity) {
    val coroutineScope = rememberCoroutineScope()
    val canSave by aggregator.rememberCanPublishPartial(ACCESS_CONTROL_PARTIAL_ID, entity)

    var loading by mutableStateOf(false)

    var savedAccessControls by remember { mutableStateOf<EntityAccessControl?>(null) }
    var updatedAccessControls by remember { mutableStateOf<EntityAccessControl?>(null) }
    var override by remember { mutableStateOf(false) }

    var saving by mutableStateOf(false)

    fun getDefaultAccessControl(): EntityAccessControl {
        val entityAccessControls: EntityAccessControl? = entity["accessControl"]?.let {
            json.decodeFromJsonElement(it)
        }

        return EntityAccessControl(
            visible = entityAccessControls?.visible ?: true,
            discoverable = entityAccessControls?.discoverable ?: true,
            recommendable = entityAccessControls?.recommendable ?: true
        )
    }

    LaunchedEffect(entity.id, entity.type, entity.version) {
        loading = true


        val accessControllableEntity =
            aggregator.clientApi.getIncomingEntityUi(
                entity.id, entity.type, entity.version
            ).partials?.find {
                it.partialId == "access-control"
            }

        savedAccessControls = accessControllableEntity?.data?.jsonObject?.get("accessControl")?.let {
            json.decodeFromJsonElement<EntityAccessControl>(it)
        }
        updatedAccessControls = savedAccessControls ?: getDefaultAccessControl()
        if (savedAccessControls != null) {
            override = true
        }
        loading = false
    }

    suspend fun saveAccessControls(accessControl: EntityAccessControl): Boolean {
        try {
            saving = true

            val viewerEmail = aggregator.viewer.email
            val partialUpdate = PartialUpdate(
                type = ContentUpdateType.UPDATE,
                timestamp = Clock.System.now(),
                modification = buildJsonObject {
                    put("id", entity.id)
                    put("type", entity.type)
                    put("version", entity.version)
                    put("accessControl", json.encodeToJsonElement(accessControl))
                },
                partialId = ACCESS_CONTROL_PARTIAL_ID,
                operations = listOf(
                    PartialUpdateOperation(
                        type = PartialUpdateOperationType.REPLACE,
                        sourcePath = "$.accessControl",
                        targetPath = "$.accessControl"
                    )
                ),
                auditContext = viewerEmail?.let {
                    AuditContext(viewerEmail)
                }
            )
            aggregator.clientApi.postPartialContentUpdate(
                partialUpdate
            )
            savedAccessControls = accessControl
            updatedAccessControls = accessControl
            override = true
            aggregator.notifications.info("Access controls saved")
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (t: Throwable) {
            val error = intakeErrorMessage("update access controls for this entity", t)
            aggregator.notifications.error(error.message, error.description)
        } finally {
            saving = false
        }
        return false
    }

    Div({
        style {
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
            padding(1.em)
        }
    }) {
        if (loading) {
            spinner()
        } else {
            simpleCheckBox(
                "Override access controls?", override
            ) { selected ->
                override = selected
            }

            Div({
                style {
                    marginLeft(2.em)
                    marginTop(5.px)
                }
            }) {
                simpleCheckBox(
                    "Visible", updatedAccessControls?.visible ?: false,
                    enabled = override
                ) { selected ->
                    updatedAccessControls = updatedAccessControls!!.copy(visible = selected)
                }

                simpleCheckBox(
                    "Discoverable", updatedAccessControls?.discoverable ?: false,
                    enabled = override
                ) { selected ->
                    updatedAccessControls = updatedAccessControls!!.copy(discoverable = selected)
                }

                simpleCheckBox(
                    "Recommendable", updatedAccessControls?.recommendable ?: false,
                    enabled = override
                ) { selected ->
                    updatedAccessControls = updatedAccessControls!!.copy(recommendable = selected)
                }
            }

            if (override && updatedAccessControls != null && canSave) {
                Div({
                    style {
                        margin(1.em, 0.px)
                    }
                }) {
                    button {
                        primary = true
                        title = "Save"
                        showProgressOnAction = true
                        enabled = !saving
                        action {
                            updatedAccessControls?.let { controls ->
                                coroutineScope.launch {
                                    saveAccessControls(controls)
                                }
                            }
                        }
                    }
                    button("Save") {
                        if (!saving) {
                            updatedAccessControls?.let { controls ->
                                coroutineScope.launch {
                                    saveAccessControls(controls)
                                }
                            }
                        }
                    }
                    button("Reset") {
                        if (!saving) {
                            updatedAccessControls = savedAccessControls ?: getDefaultAccessControl()
                            override = savedAccessControls != null
                        }
                    }
                }
            }
        }
    }
}


